// Decorative effects never change content, links or form state.
export function setupWelcomeEffects (root) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')
  let observer = null
  let pointerFrame = null
  let scrollFrame = null
  let pendingPointer = null
  let listeners = []
  let disposed = false

  function updateProgress () {
    if (disposed || scrollFrame !== null) return
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null
      const distance = root.scrollHeight - root.clientHeight
      root.style.setProperty('--scroll-progress', distance > 0 ? Math.max(0, Math.min(1, root.scrollTop / distance)) : 0)
    })
  }

  function clearPointer (element) {
    if (pendingPointer && pendingPointer.element === element) pendingPointer = null
    element.classList.remove('fx-pointer-active')
    for (const property of ['--pointer-x', '--pointer-y', '--tilt-x', '--tilt-y']) element.style.removeProperty(property)
  }

  function stopMotion () {
    if (observer) observer.disconnect()
    observer = null
    if (pointerFrame !== null) window.cancelAnimationFrame(pointerFrame)
    pointerFrame = null
    pendingPointer = null
    listeners.forEach(remove => remove())
    listeners = []
    root.classList.remove('fx-ready')
    root.querySelectorAll('.fx-pending, .fx-visible, .fx-interactive').forEach(element => {
      clearPointer(element)
      element.classList.remove('fx-pending', 'fx-visible', 'fx-interactive')
    })
  }

  function startMotion () {
    stopMotion()
    if (disposed || reduceMotion.matches) return
    root.classList.add('fx-ready')
    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          entry.target.classList.remove('fx-pending')
          entry.target.classList.add('fx-visible')
          observer.unobserve(entry.target)
        })
      }, { root, threshold: 0.08 })
      root.querySelectorAll('.waybill-card, .data-item, .feat-card, .alert-box, .bottom-cta').forEach(element => {
        element.classList.add('fx-pending')
        observer.observe(element)
      })
    }
    if (!finePointer.matches) return
    root.querySelectorAll('.hero, .waybill-card, .data-item, .feat-card').forEach(element => {
      element.classList.add('fx-interactive')
      const move = event => {
        if (event.pointerType !== 'mouse') return
        const bounds = element.getBoundingClientRect()
        pendingPointer = { element, x: event.clientX - bounds.left, y: event.clientY - bounds.top, width: bounds.width, height: bounds.height }
        if (pointerFrame !== null) return
        pointerFrame = window.requestAnimationFrame(() => {
          pointerFrame = null
          const point = pendingPointer
          pendingPointer = null
          if (!point || disposed) return
          point.element.classList.add('fx-pointer-active')
          point.element.style.setProperty('--pointer-x', point.x + 'px')
          point.element.style.setProperty('--pointer-y', point.y + 'px')
          point.element.style.setProperty('--tilt-x', ((0.5 - point.y / point.height) * 8).toFixed(2) + 'deg')
          point.element.style.setProperty('--tilt-y', ((point.x / point.width - 0.5) * 8).toFixed(2) + 'deg')
        })
      }
      const leave = () => clearPointer(element)
      element.addEventListener('pointermove', move, { passive: true })
      element.addEventListener('pointerleave', leave)
      listeners.push(() => { element.removeEventListener('pointermove', move); element.removeEventListener('pointerleave', leave) })
    })
  }

  const visibility = () => root.classList.toggle('fx-paused', document.hidden)
  const mediaQueries = [reduceMotion, finePointer]
  mediaQueries.forEach(query => {
    if (query.addEventListener) query.addEventListener('change', startMotion)
    else query.addListener(startMotion)
  })
  root.addEventListener('scroll', updateProgress, { passive: true })
  document.addEventListener('visibilitychange', visibility)
  window.addEventListener('resize', updateProgress, { passive: true })
  startMotion()
  visibility()
  updateProgress()

  return () => {
    disposed = true
    stopMotion()
    if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame)
    root.removeEventListener('scroll', updateProgress)
    document.removeEventListener('visibilitychange', visibility)
    window.removeEventListener('resize', updateProgress)
    mediaQueries.forEach(query => {
      if (query.removeEventListener) query.removeEventListener('change', startMotion)
      else query.removeListener(startMotion)
    })
    root.classList.remove('fx-paused')
    root.style.removeProperty('--scroll-progress')
  }
}
